"use client"
import MapBoxMapWrapper from "@/components/shared/MapBoxMapWrapper";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import LocatorSearchComponent from "@/components/locator/LocatorSearchComponent";
import LocatorSearchResultsComponent from "@/components/locator/LocatorSearchResultsComponent";
import LocatorSearchResultPopout from "@/components/locator/LocatorSearchResultPopout";
import LocatorSearchMenu from "@/components/locator/LocatorSearchMenu";
import useApi from "@/components/api/useApi";
import { Ratio } from "react-bootstrap";
import { useAppData } from "@/lib/providers/AppDataProvider";
import ServiceProfileFilters from "@/components/locator/ServiceProfileFilters";
import BusinessProfileFilters from "@/components/locator/BusinessProfileFilters";
import LocatorCategoryPanel from "@/components/locator/LocatorCategoryPanel";
import { applyBestUseScores } from "@/components/locator/applyBestUseScores";
import MapBoxMapPopup from "@/components/shared/MapBoxMapPopup";
import LocatorSearchResultPreview from "@/components/locator/LocatorSearchResultPreview";
import UralityImage from "@/components/shared/UralityImage";
import LocatorFullscreenControl from "@/components/locator/LocatorFullscreenControl";
import LocatorBrandBadge from "@/components/locator/LocatorBrandBadge";
import useBoundaryDefaultCenter from "@/components/locator/useBoundaryDefaultCenter";
import useLocatorEventMarkers, { LocatorEventPopup } from "@/components/locator/useLocatorEventMarkers";
import RELocatorFilters from "@/components/locator/re/RELocatorFilters";
import RESearchResultPreview from "@/components/locator/re/RESearchResultPreview";
import REListingModal from "@/components/locator/re/REListingModal";
import {
  RE_LAYER_AVAILABLE,
  RE_LAYER_CATEGORIES,
  RE_LAYER_SEARCH_TYPE,
  buildREResults,
  decorateRELayerFeatures,
  filterREFeatures,
  reLayerCategoryFor,
  reLayerSearchOption
} from "@/components/locator/re/reLayer";
import {
  PIN_LAYER_POINT_SEARCH_TYPE,
  PIN_LAYER_SEARCH_TYPE,
  buildPinLayerFeatures,
  buildPinLayerPointSearchOptions,
  buildPinLayerResults,
  matchPinLayerResults,
  pinLayerCategoryGroup,
  pinLayerSearchOption
} from "@/components/locator/pinLayer";
import * as turf from "@turf/turf";
import "@/components/crm/serviceProfile/ServiceProfileListContainer.css";

import { useMediaQuery } from "react-responsive";
import { useCurrentLocation } from "@/lib/providers/GeolocationProvider";

let moment = require("moment");

// search text shown while a building outline is selected, so the search's clear (x) can reset it
const SELECTED_BUILDING_TEXT = "Selected Building";

const LocatorContainer = ({
  profile,
  community,
  searchMode,
  token,
  backgroundGeoJson,
  category,
  showOpenNow = true,
  systemLayers,
  pinLayers,
  eventEntries,
  allowFullscreen = false,
  brandBadge,
  // business mode with BUILDING_FOOTPRINTS shown: clicking a building lists its businesses
  enableBuildingSelection = false,
  // business mode: the community's RE listings drawn alongside the businesses, under the "Development" categories
  showRealEstateLayer = false,
  // { pitch, bearing } for the 2.5D view (see MapBoxMap), else null
  perspective = null
}) => {
  const isSm = useMediaQuery({ query: "(max-width: 767.98px)" });
  const { currentLocation } = useCurrentLocation();

  const [defaultCenter, setDefaultCenter] = useState(null);
  const [boundingBox, setBoundingBox] = useState(null);
  const [mapPopup, setMapPopup] = useState(null);

  const [searchOptions, setSearchOptions] = useState(null);
  const [businessSearchIndex, setBusinessSearchIndex] = useState(null);
  const [allSearchOptions, setAllSearchOptions] = useState(null);

  const [searchText, setSearchText] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);

  const [selectedSearchOption, setSelectedSearchOption] = useState(null);
  const [textQuery, setTextQuery] = useState(null);

  const [searchResults, setSearchResults] = useState(null);
  const [filteredResults, setFilteredResults] = useState(null);
  const [itemHovered, setItemHovered] = useState(null);
  const [itemSelected, setItemSelected] = useState(null);
  const [businessMultipleLocations, setBusinessMultipleLocations] = useState(
    false
  );

  const api = useApi();

  const { flattenedProfileTypes: profileTypesFlattened, flattenedVendorTypes: vendorTypesFlattened, flattenedServiceCategories: serviceCategoriesFlattened } = useAppData();

  const [locatorGeoJson, setLocatorGeoJson] = useState(null);
  const [locatorFilter, setLocatorFilter] = useState(null);
  const [filteredLocatorGeoJson, setFilteredLocatorGeoJson] = useState(null);
  const [currentFilter, setCurrentFilter] = useState(null);
  const [pinStyles, setPinStyles] = useState(null);

  // buildingId -> locator location ids inside it
  const [buildingIndex, setBuildingIndex] = useState(null);
  // { id, results } where results are every expanded location of the building's businesses (null while loading)
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);

  // the real estate layer's features, styled as category markers
  const [reLayerFeatures, setRELayerFeatures] = useState(null);
  // RELocatorFilters' current filter, applied while "Available spaces" is selected
  const [reLayerFilter, setRELayerFilter] = useState(null);
  // the RE listing shown in the listing modal
  const [reListingSelected, setREListingSelected] = useState(null);

  useEffect(() => {
    if (profile && showRealEstateLayer) {
      api
        .cms_getRELocatorGeoJson(profile.resourceName, token)
        .then(geoJson => {
          setRELayerFeatures(decorateRELayerFeatures(geoJson));
        })
        .catch(error => {
          console.log("Unable to load real estate layer", error);
        });
    }
  }, [profile, showRealEstateLayer, token]);

  // the configured pin layers' points, as result items and as locator features
  const pinLayerResults = useMemo(() => buildPinLayerResults(pinLayers), [
    pinLayers
  ]);
  const pinLayerFeatures = useMemo(
    () => buildPinLayerFeatures(pinLayerResults),
    [pinLayerResults]
  );

  // the businesses plus the real estate and pin layers; locatorFilter ids span all of them
  const mapGeoJson = useMemo(
    () =>
      locatorGeoJson && (reLayerFeatures?.length || pinLayerFeatures.length)
        ? {
            ...locatorGeoJson,
            features: locatorGeoJson.features.concat(
              reLayerFeatures ?? [],
              pinLayerFeatures
            )
          }
        : locatorGeoJson,
    [locatorGeoJson, reLayerFeatures, pinLayerFeatures]
  );

  const reLayerCategory =
    selectedSearchOption?.searchType === RE_LAYER_SEARCH_TYPE
      ? reLayerCategoryFor(selectedSearchOption.value)
      : null;

  const selectedPinLayerId =
    selectedSearchOption?.searchType === PIN_LAYER_SEARCH_TYPE
      ? selectedSearchOption.value
      : null;

  // the category panel's groups after the macro types: "Development" (real estate layer) and the pin layers
  const extraCategoryGroups = useMemo(
    () =>
      [
        showRealEstateLayer && {
          name: "Development",
          categories: RE_LAYER_CATEGORIES.map(category => ({
            key: category.key,
            label: category.label,
            color: category.color,
            icon: category.icon,
            option: reLayerSearchOption(category)
          }))
        },
        pinLayerCategoryGroup(pinLayers)
      ].filter(Boolean),
    [showRealEstateLayer, pinLayers]
  );

  // a selected pin layer category lists its points
  useEffect(() => {
    if (!selectedPinLayerId) return;
    setSelectedBuilding(null);
    const results = pinLayerResults.filter(
      result => result.pinLayerId === selectedPinLayerId
    );
    setSearchResults(results);
    setFilteredResults(results);
    setLocatorFilter(results.map(result => result.id));
  }, [selectedPinLayerId, pinLayerResults]);

  // a selected "Development" category lists its listings, narrowed by RELocatorFilters for "Available spaces"
  useEffect(() => {
    if (!reLayerCategory) return;
    setSelectedBuilding(null);
    const features = (reLayerFeatures ?? []).filter(
      feature => feature.properties.reLayerCategory === reLayerCategory.key
    );
    const filtered =
      reLayerCategory.key === RE_LAYER_AVAILABLE
        ? filterREFeatures(features, reLayerFilter)
        : features;
    const filteredREResults = buildREResults(filtered);
    setSearchResults(buildREResults(features));
    setFilteredResults(filteredREResults);
    setLocatorFilter(filteredREResults.map(result => result.id));
  }, [reLayerCategory?.key, reLayerFeatures, reLayerFilter]);

  const lookupREListing = id => {
    api
      .cms_getREListing(profile.resourceName, id, token)
      .then(listing => {
        setREListingSelected(listing);
      })
      .catch(error => {
        console.error("Error getting RE listing", error);
      });
  };

  useEffect(() => {
    if (profile && enableBuildingSelection) {
      api
        .cms_getLocatorBuildingIndex(profile.resourceName, token)
        .then(index => {
          setBuildingIndex(index ?? {});
        })
        .catch(error => {
          console.log("Unable to load building index", error);
          setBuildingIndex({});
        });
    }
  }, [profile, enableBuildingSelection, token]);

  useEffect(() => {
    if (profile && searchMode === "business") {
      api
        .getCommunityMapConfiguration(profile.resourceName, token)
        .then(config => {
          setPinStyles(
            Object.fromEntries(
              config.macroTypes.map(e => [
                e.macroType,
                { color: e.color, icon: e.icon }
              ])
            )
          );
        })
        .catch(error => {
          console.log("Unable to load map pin styles", error);
        });
    }
  }, [profile, searchMode, token]);

  useEffect(() => {
    if (profile && searchMode && !locatorGeoJson) {
      api
        .cms_getLocatorGeoJson(profile.resourceName, searchMode, token)
        .then(geoJson => {
          setLocatorGeoJson(geoJson);
          if (searchMode === "business" || searchMode === "service") {
            let searchableBusinessIndex = buildSearchableBusinessIndex(geoJson);
            setBusinessSearchIndex(searchableBusinessIndex);
          } else {
            setBusinessSearchIndex([]);
          }
          setBoundingBox(geoJson?.bbox);
          let turfCenter = turf.center(geoJson);
          if( geoJson?.bbox ) {
            setDefaultCenter({
              fitBounds: geoJson.bbox,
              fitBoundsOptions: { padding: { top: 20, bottom: 20, left: 20, right: 20 } }
            });
          } else {
            setDefaultCenter({
              longitude: turfCenter.geometry.coordinates[0],
              latitude: turfCenter.geometry.coordinates[1]
            });
          }
        });
    }
  }, [profile, searchMode, token, locatorGeoJson]);

  const buildSearchableBusinessIndex = geoJson => {
    const searchableBusinessIndex = geoJson.features?.map(feature => {
      const properties = feature.properties;
      return {
        id: properties.id,
        value: properties.resourceName,
        label: properties.displayName,
        searchTerms: [properties.displayName],
        icon: properties.icon ?? "location-pin",
        searchType: "BUSINESS",
        type: "BUSINESS"
      };
    });
    return searchableBusinessIndex;
  };

  useEffect(() => {
    if (
      profile &&
      searchMode &&
      profileTypesFlattened &&
      vendorTypesFlattened &&
      serviceCategoriesFlattened &&
      category
    ) {
      let searchOption = determineSearchOptionFromCategory(
        category,
        searchMode
      );
      if (searchOption) {
        setSelectedSearchOption(searchOption);
        setSearchText(searchOption.label);
      }
    }
  }, [
    profile,
    category,
    searchMode,
    profileTypesFlattened,
    vendorTypesFlattened,
    serviceCategoriesFlattened
  ]);

  const determineSearchOptionFromCategory = (category, mode) => {
    if (mode === "service") {
      return serviceCategoriesFlattened.find(
        option => option.value === category
      );
    } else if (mode === "vendor") {
      return vendorTypesFlattened.find(option => option.value === category);
    } else if (mode === "business") {
      return profileTypesFlattened.find(option => option.value === category);
    }
  };

  const onServiceProfileFilterChange = (
    selectedAction,
    selectedCondition,
    deliveryFlag,
    pickupFlag
  ) => {
    setCurrentFilter({
      selectedAction: selectedAction ?? null,
      selectedCondition: selectedCondition ?? null,
      deliveryFlag: deliveryFlag ?? null,
      pickupFlag: pickupFlag ?? null
    });
    if (searchResults) {
      const filtered = searchResults.filter(result => {
        const satisfiesDelivery = deliveryFlag
          ? result.serviceProfile?.delivery != null
          : true;
        const satisfiesPickup = pickupFlag
          ? result.serviceProfile?.pickup != null
          : true;
        let hasMatchingListing = false;
        if (selectedSearchOption) {
          hasMatchingListing = result.serviceProfile?.serviceListings?.some(
            listing => {
              if (selectedAction && selectedCondition) {
                return (
                  (listing.categories?.includes(selectedSearchOption.value) ||
                    listing.items?.includes(selectedSearchOption.value)) &&
                  listing.actions?.includes(selectedAction) &&
                  listing.conditions?.includes(selectedCondition)
                );
              } else if (selectedAction) {
                return (
                  (listing.categories?.includes(selectedSearchOption.value) ||
                    listing.items?.includes(selectedSearchOption.value)) &&
                  listing.actions?.includes(selectedAction)
                );
              } else if (selectedCondition) {
                return (
                  (listing.categories?.includes(selectedSearchOption.value) ||
                    listing.items?.includes(selectedSearchOption.value)) &&
                  listing.conditions?.includes(selectedCondition)
                );
              } else {
                return true;
              }
            }
          );
        } else {
          hasMatchingListing = result.serviceProfile?.serviceListings?.some(
            listing => {
              if (selectedAction && selectedCondition) {
                return (
                  listing.actions?.includes(selectedAction) &&
                  listing.conditions?.includes(selectedCondition)
                );
              } else if (selectedAction) {
                return listing.actions?.includes(selectedAction);
              } else if (selectedCondition) {
                return listing.conditions?.includes(selectedCondition);
              } else {
                return true;
              }
            }
          );
        }
        return hasMatchingListing && satisfiesDelivery && satisfiesPickup;
      });
      setFilteredResults(filtered);
      setLocatorFilter(filtered.map(result => result.id));
    } else {
      setFilteredResults(null);
    }
  };

  const onBusinessProfileFilterChange = (selectedAmenities, openNow) => {
    setCurrentFilter({
      selectedAmenities: selectedAmenities ?? null,
      openNow: openNow ?? null
    });
    if (searchResults) {
      let filtered = searchResults;
      if (selectedAmenities && selectedAmenities.length > 0) {
        filtered = searchResults.filter(result => {
          return result.amenities?.some(amenity =>
            selectedAmenities.includes(amenity)
          );
        });
      }
      if (openNow) {
        filtered = openNowFilter(filtered);
      }
      setLocatorFilter(filtered.map(result => result.id));
      setFilteredResults(filtered);
    } else {
      setFilteredResults(null);
    }
  };

  const openNowFilter = filteredResults => {
    var now = moment();
    return filteredResults.filter(
      fp =>
        fp &&
        fp.location &&
        fp.location.hours &&
        openNow(now, fp.location.hours)
    );
  };

  const openNow = (now, openHours) => {
    var hoursObj = JSON.parse(openHours);
    // get today, time and day
    var utcOffset = moment.parseZone(now).utcOffset();
    var minutes = utcOffset * -1;
    var dow = now.format("dddd");
    var dowCaps = dow.toUpperCase();
    // find the object that contains the day
    // find the object that contains the day
    var dayObject = hoursObj.periods.find(
      period => period.open.day === dowCaps
    );
    if (dayObject && dayObject.open && dayObject.close) {
      // create the day from the name of the day
      var startOfDay = moment()
        .hours(dayObject.open.time.hour)
        .minutes(dayObject.open.time.minute);
      // .add(minutes, "minutes");
      var endOfDay = moment()
        .hours(dayObject.close.time.hour)
        .minutes(dayObject.close.time.minute);
      // .add(minutes, "minutes");
      if (dayObject.open.day !== dayObject.close.day) {
        endOfDay = endOfDay.add(1, "days");
      }
      if (now.isBetween(startOfDay, endOfDay)) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (mapGeoJson) {
      if (locatorFilter) {
        setFilteredLocatorGeoJson({
          ...mapGeoJson,
          features: mapGeoJson.features.filter(feature =>
            locatorFilter.includes(feature.properties.id)
          )
        });
      } else {
        setFilteredLocatorGeoJson(mapGeoJson);
      }
    }
  }, [mapGeoJson, locatorFilter]);

  // the locations currently drawn on the map, i.e. after search and filters
  const visibleLocationIds = useMemo(
    () =>
      new Set(
        (filteredLocatorGeoJson?.features ?? []).map(
          feature => feature.properties.id
        )
      ),
    [filteredLocatorGeoJson]
  );

  // buildings holding at least one visible location; the others aren't drawn
  const visibleBuildingIds = useMemo(() => {
    if (!enableBuildingSelection) return null;
    if (!buildingIndex) return [];
    return Object.keys(buildingIndex).filter(buildingId =>
      buildingIndex[buildingId].some(locationId =>
        visibleLocationIds.has(locationId)
      )
    );
  }, [enableBuildingSelection, buildingIndex, visibleLocationIds]);

  // the selected building's businesses that pass the current filter, one row per location in the building
  const buildingResults = useMemo(() => {
    if (!selectedBuilding?.results) return null;
    const locationIds = buildingIndex?.[selectedBuilding.id] ?? [];
    return selectedBuilding.results.filter(
      result =>
        locationIds.includes(result.id) && visibleLocationIds.has(result.id)
    );
  }, [selectedBuilding, buildingIndex, visibleLocationIds]);

  const clearSelectedBuilding = () => {
    setSelectedBuilding(null);
    setItemSelected(null);
    setSearchText(current => (current === SELECTED_BUILDING_TEXT ? "" : current));
  };

  useEffect(() => {
    if (
      selectedBuilding &&
      visibleBuildingIds &&
      !visibleBuildingIds.includes(selectedBuilding.id)
    ) {
      clearSelectedBuilding();
    }
  }, [selectedBuilding, visibleBuildingIds]);

  const onSystemLayerFeatureClick = (type, feature) => {
    if (type !== "BUILDING_FOOTPRINTS" || !feature?.id) return;
    const buildingId = feature.id;
    setMapPopup(null);
    setItemSelected(null);
    setSearchText(current => current || SELECTED_BUILDING_TEXT);
    setSelectedBuilding({ id: buildingId, results: null });
    api
      .cms_getLocatorBuildingProfiles(profile.resourceName, buildingId, token)
      .then(results => {
        const expandedResults = buildExpandedResults(results ?? []);
        setSelectedBuilding(current =>
          current?.id === buildingId
            ? { id: buildingId, results: expandedResults }
            : current
        );
        const locationIds = buildingIndex?.[buildingId] ?? [];
        const visible = expandedResults.filter(
          result =>
            locationIds.includes(result.id) && visibleLocationIds.has(result.id)
        );
        if (visible.length === 1) {
          setItemSelected(visible[0]);
        }
      })
      .catch(error => {
        console.error("Error loading building businesses", error);
        setSelectedBuilding(current =>
          current?.id === buildingId ? { id: buildingId, results: [] } : current
        );
      });
  };

  const onEventClicked = useCallback(
    (entry, event) => {
      setSelectedEventId(event.id);
      setMapPopup(
        <LocatorEventPopup
          resourceName={profile.resourceName}
          entry={entry}
          event={event}
        />
      );
    },
    [profile]
  );

  const { defaultCenter: mapDefaultCenter, boundingBox: boundaryBoundingBox } =
    useBoundaryDefaultCenter(systemLayers, defaultCenter);

  const eventMarkers = useLocatorEventMarkers(
    eventEntries,
    selectedEventId,
    onEventClicked
  );

  const mapPopupElement = (mapWithEvt, item) => {
    return (
      <MapBoxMapPopup
        latitude={mapWithEvt.lngLat.lat}
        longitude={mapWithEvt.lngLat.lng}
        anchor="bottom"
      >
        <div style={{ position: "relative" }}>
          {item.coverPhotoId && (
            <Ratio
              aspectRatio="16x9"
              className="locator-search-result-thumbnail"
            >
              <UralityImage
                imageId={item.coverPhotoId}
                className="locator-result-cover-image"
                alt={item.displayName}
                size="md"
              />
            </Ratio>
          )}
          <LocatorSearchResultPreview
            item={item}
            selectedSearchOption={selectedSearchOption}
            searchMode={searchMode}
          />
          <button
            style={{ marginTop: "1rem" }}
            className="a"
            onClick={() => {
              setMapPopup(null);
              setSearchText(item.displayName);
              setItemSelected(item);
            }}
          >
            More Info..
          </button>
        </div>
      </MapBoxMapPopup>
    );
  };

  const reMapPopupElement = (mapWithEvt, item) => (
    <MapBoxMapPopup
      anchor="bottom"
      latitude={mapWithEvt.lngLat.lat}
      longitude={mapWithEvt.lngLat.lng}
    >
      <div style={{ position: "relative" }}>
        <RESearchResultPreview
          item={item}
          showThumbnail={item.selectedImages?.length > 0}
        />
        <button
          style={{ marginTop: "1rem" }}
          className="a"
          onClick={() => {
            setMapPopup(null);
            lookupREListing(item.id);
          }}
        >
          More Info..
        </button>
      </div>
    </MapBoxMapPopup>
  );

  const locatorFeatureClicked = (mapWithEvt, properties) => {
    // console.log("locatorFeatureClicked", mapWithEvt, properties);
    if (properties.reListing) {
      // mapbox flattens array properties to JSON strings, so use the layer's own feature
      const feature = reLayerFeatures?.find(
        f => f.properties.id === properties.id
      );
      if (feature) {
        setSelectedEventId(null);
        setMapPopup(reMapPopupElement(mapWithEvt, feature.properties));
      }
      return;
    }
    if (properties.pinLayerPoint) {
      const item = pinLayerResults.find(result => result.id === properties.id);
      if (item) {
        setSelectedEventId(null);
        setMapPopup(mapPopupElement(mapWithEvt, item));
      }
      return;
    }
    let item = null;
    // console.log("searchResults", searchResults);
    if (searchResults && searchResults.length > 0) {
      item = searchResults.find(result => result.id === properties.id);
    } 
    // console.log("item after searchResults", item);
    if (item) {
      setMapPopup(mapPopupElement(mapWithEvt, item));
    } else {
      api
        .cms_getChildProfile(
          profile.resourceName,
          properties.resourceName,
          token
        )
        .then(childProfile => {
          if( childProfile.locations && childProfile.locations.length > 0) {
            childProfile["location"] = childProfile.locations.find(location => location.id === properties.id);
          } else {
            childProfile["location"] = childProfile.locations[0];
          }
          setMapPopup(mapPopupElement(mapWithEvt, childProfile));
        })
        .catch(error => {
          console.error("Error getting child profile", error);
        });
    }
  };

  const onMapClicked = (lat, lon) => {
    // console.log("onMapClicked", lat, lon);
    setMapPopup(null);
    setSelectedEventId(null);
  };

  const onSearchTextChanged = searchText => {
    setSelectedSearchOption(null);
    setTextQuery(null);
    setSearchResults(null);
    setFilteredResults(null);
    setItemHovered(null);
    setItemSelected(null);
    setBusinessMultipleLocations(false);
    setSelectedBuilding(null);
    setSearchText(searchText);
    if (searchMode === "business") {
      setCurrentFilter({ openNow: showOpenNow });
    } else {
      setCurrentFilter(null);
    }
    if (!searchText || searchText.length === 0) {
      setLocatorFilter(
        mapGeoJson.features.map(feature => feature.properties.id)
      );
    }
  };

  useEffect(() => {
    if (
      searchMode &&
      serviceCategoriesFlattened &&
      vendorTypesFlattened &&
      profileTypesFlattened
    ) {
      if (searchMode === "service") {
        setSearchOptions(serviceCategoriesFlattened);
      } else if (searchMode === "vendor") {
        setSearchOptions(vendorTypesFlattened);
      } else if (searchMode === "business") {
        setSearchOptions(
          profileTypesFlattened.filter(
            option => option.type === "BUSINESS" && option.searchTerms != null
          )
        );
      }
    }
  }, [
    searchMode,
    serviceCategoriesFlattened,
    vendorTypesFlattened,
    profileTypesFlattened
  ]);

  useEffect(() => {
    if (selectedSearchOption || textQuery) {
      setSelectedBuilding(null);
    }
    if (
      selectedSearchOption?.searchType === RE_LAYER_SEARCH_TYPE ||
      selectedSearchOption?.searchType === PIN_LAYER_SEARCH_TYPE
    ) {
      // listed locally from the real estate / pin layers (see the reLayerCategory and selectedPinLayerId effects)
      return;
    }
    if (selectedSearchOption) {
      let mapToField = null;
      let operand = null;
      if (searchMode === "service") {
        if (selectedSearchOption.group) {
          mapToField = "SERVICE_LISTING_ITEM";
          operand = "IN";
        } else {
          mapToField = "SERVICE_LISTING_CATEGORY";
          operand = "IN";
        }
      } else if (searchMode === "vendor") {
        if (selectedSearchOption.group) {
          mapToField = "BUSINESS_VENDOR_PRODUCTS";
          operand = "IN";
        } else {
          mapToField = "BUSINESS_VENDOR_MACRO_TYPE";
          operand = "EQUAL";
        }
      } else if (searchMode === "business") {
        if (selectedSearchOption.group) {
          mapToField = "BUSINESS_TYPES";
          operand = "IN";
        } else {
          mapToField = "BUSINESS_MACRO_TYPE";
          operand = "EQUAL";
        }
      }

      if (mapToField) {
        api
          .crm_publicDataQuery(
            profile.resourceName,
            {
              queryObject: "BUSINESS",
              criteria: [
                {
                  mapToField: mapToField,
                  operand: operand,
                  value: selectedSearchOption.value
                }
              ]
            },
            token
          )
          .then(results => {
            let expandedResults = buildExpandedResults(results);
            // You can now use expandedResults instead of results, if needed.
            if (searchMode === "service") {
              applyBestUseScores(expandedResults, selectedSearchOption);
            }
            setSearchResults(expandedResults);
            if (currentFilter && searchMode === "business") {
              onBusinessProfileFilterChange(
                currentFilter.selectedAmenities ?? [],
                currentFilter.openNow
              );
            }
            // if (currentFilter) {
            //   onServiceProfileFilterChange(
            //     currentFilter.selectedAction,
            //     currentFilter.selectedCondition,
            //     currentFilter.deliveryFlag,
            //     currentFilter.pickupFlag
            //   );
            // }
            setLocatorFilter(expandedResults.map(result => result.id));
            setFilteredResults(expandedResults);
          })
          .catch(error => {
            console.error("PUBLIC QUERY error", error);
          });
      }
    } else if (textQuery) {
      api
        .crm_publicDataQuery(
          profile.resourceName,
          {
            queryObject: "BUSINESS",
            searchText: textQuery
          },
          token
        )
        .then(results => {
          let expandedResults = buildExpandedResults(results).concat(
            matchPinLayerResults(pinLayerResults, textQuery)
          );
          setSearchResults(expandedResults);
          setFilteredResults(expandedResults);
          setLocatorFilter(expandedResults.map(result => result.id));
        });
    }
  }, [selectedSearchOption, textQuery]);

  const buildExpandedResults = results => {
    let expandedResults = [];
    for (const result of results) {
      const locations =
        result.locations && Array.isArray(result.locations)
          ? result.locations
          : [];
      if (locations.length > 0) {
        for (const location of locations) {
          expandedResults.push({
            ...result,
            location: location,
            id: location.id
          });
        }
      }
    }
    return expandedResults;
  };

  useEffect(() => {
    if (searchOptions && businessSearchIndex && !allSearchOptions) {
      // console.log("searchOptions", searchOptions);
      // console.log("businessSearchIndex", businessSearchIndex);
      // remove duplicates by id from businessSearchIndex
      const unique = {};
      const deduped = [];
      if (businessSearchIndex) {
        for (const item of businessSearchIndex) {
          if (item.value && !unique[item.value]) {
            unique[item.value] = true;
            deduped.push(item);
          }
        }
      }
      setAllSearchOptions(searchOptions.concat(deduped));
    }
  }, [searchOptions, businessSearchIndex, allSearchOptions]);

  // the pin layers and their points are searchable too; they load separately from the businesses
  const searchMenuOptions = useMemo(
    () =>
      allSearchOptions && pinLayers?.length
        ? allSearchOptions.concat(
            pinLayers.map(pinLayerSearchOption),
            buildPinLayerPointSearchOptions(pinLayerResults)
          )
        : allSearchOptions,
    [allSearchOptions, pinLayers, pinLayerResults]
  );

  const searchResultsOpen =
    searchResults != null ||
    selectedBuilding != null ||
    (itemSelected && !selectedSearchOption);
  const previewOpen =
    searchText.length > 0 &&
    !itemSelected &&
    searchResults == null &&
    selectedBuilding == null;
  // business categories open under the search bar while it has focus and nothing has been searched yet
  const showCategoryPanel =
    searchMode === "business" &&
    searchFocused &&
    searchText.length === 0 &&
    selectedSearchOption == null &&
    textQuery == null &&
    searchResults == null &&
    !itemSelected &&
    selectedBuilding == null;
  if (allSearchOptions) {
    return (
      <>
        <div
          className={`locator-container-header${
            searchResultsOpen ? " search-results-open" : ""
          }`}
        >
          <LocatorSearchComponent
            searchMode={searchMode}
            defaultText={selectedSearchOption?.label ?? searchText}
            onChange={onSearchTextChanged}
            searchResultsOpen={searchResultsOpen}
            previewOpen={previewOpen}
            onEnterPressed={val => {
              setSearchText(val);
              setTextQuery(val);
            }}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
          {(searchMode === "service" ||
            searchMode === "business" ||
            allowFullscreen) && (
            <div
              className={`search-filters-container${
                searchResultsOpen ? " search-results-open" : ""
              }`}
            >
              {allowFullscreen && <LocatorFullscreenControl />}
              {searchMode === "service" && (
                <ServiceProfileFilters
                  onFilterChange={onServiceProfileFilterChange}
                  searchResults={searchResults}
                  onCategorySelected={category => {
                    setSelectedSearchOption(category);
                  }}
                />
              )}
              {searchMode === "business" &&
                reLayerCategory?.key === RE_LAYER_AVAILABLE && (
                  <RELocatorFilters
                    allowAnySaleOrLease={true}
                    onFilterChange={setRELayerFilter}
                    searchResults={searchResults}
                  />
                )}
              {searchMode === "business" &&
                !reLayerCategory &&
                !selectedPinLayerId && (
                <BusinessProfileFilters
                  showOpenNow={showOpenNow}
                  onFilterChange={onBusinessProfileFilterChange}
                  searchResults={searchResults}
                />
              )}
            </div>
          )}
          {brandBadge && <LocatorBrandBadge {...brandBadge} />}
        </div>
        {showCategoryPanel && (
          <LocatorCategoryPanel
            pinStyles={pinStyles}
            extraGroups={extraCategoryGroups}
            onCategorySelected={category => {
              setSelectedSearchOption(category);
            }}
          />
        )}
        <LocatorSearchMenu
          showTextSearch={true}
          searchOptions={searchMenuOptions}
          show={
            searchText.length > 0 &&
            selectedSearchOption == null &&
            textQuery == null &&
            !itemSelected &&
            !businessMultipleLocations &&
            selectedBuilding == null
          }
          searchText={searchText}
          onSelect={selected => {
            // console.log("onSelect", selected);
            if (selected?.searchType === "BUSINESS") {
              api
                .cms_getChildProfile(
                  profile.resourceName,
                  selected.value,
                  token
                )
                .then(childProfile => {
                  if (
                    childProfile.locations &&
                    childProfile.locations.length === 1
                  ) {
                    setItemSelected({
                      ...childProfile,
                      location: childProfile.locations[0]
                    });
                  } else {
                    setBusinessMultipleLocations(true);
                    let expandedResults = childProfile.locations.map(
                      location => ({
                        ...childProfile,
                        location: location,
                        id: location.id
                      })
                    );
                    setSearchResults(expandedResults);
                    setFilteredResults(expandedResults);
                    setLocatorFilter(expandedResults.map(result => result.id));
                  }
                  setSearchText(selected.label);
                });
            } else if (selected?.searchType === PIN_LAYER_POINT_SEARCH_TYPE) {
              const point = pinLayerResults.find(
                result => result.id === selected.value
              );
              if (point) {
                setItemSelected(point);
                setSearchText(selected.label);
              }
            } else if (selected?.searchType === "TEXT") {
              setSearchText(selected.value);
              setTextQuery(selected.value);
            }
            // else if (searchOption.searchType === "CRITERIA") {
            //   setCriteria(
            //     criteria && criteria.length > 0
            //       ? [...criteria, searchOption.value]
            //       : [searchOption.value]
            //   );
            // }
            else {
              setSelectedSearchOption(selected);
            }
          }}
          searchMode={searchMode}
        />
        <div
          className={`locator-container-results ${
            searchResultsOpen ? "show" : ""
          }`}
        >
          {selectedBuilding ? (
            <LocatorSearchResultsComponent
              key={`building-${selectedBuilding.id}`}
              community={community}
              searchResults={buildingResults}
              heading={
                buildingResults
                  ? `Businesses in this building (${buildingResults.length})`
                  : "Businesses in this building"
              }
              onClear={clearSelectedBuilding}
              emptyText="No businesses in this building match your search."
              onItemHovered={result => setItemHovered(result)}
              onItemSelected={result => setItemSelected(result)}
              selectedItem={itemSelected}
              searchMode={searchMode}
              textQuery={textQuery}
              selectedSearchOption={selectedSearchOption}
              currentLocation={currentLocation}
              currentFilter={currentFilter}
              businessMultipleLocations={true}
            />
          ) : reLayerCategory ? (
            <LocatorSearchResultsComponent
              key={`re-layer-${reLayerCategory.key}`}
              community={community}
              searchResults={filteredResults ?? searchResults}
              heading={reLayerCategory.label}
              emptyText="No listings match your search."
              onItemHovered={result => setItemHovered(result)}
              // RE listings open in REListingModal rather than the results panel's detail card
              onItemSelected={result => result && lookupREListing(result.id)}
              searchMode="re"
              textQuery={textQuery}
              selectedSearchOption={selectedSearchOption}
              currentLocation={currentLocation}
              currentFilter={reLayerFilter}
            />
          ) : (
            <LocatorSearchResultsComponent
              community={community}
              searchResults={filteredResults ?? searchResults}
              heading={selectedPinLayerId ? selectedSearchOption.label : undefined}
              onItemHovered={result => setItemHovered(result)}
              onItemSelected={result => setItemSelected(result)}
              selectedItem={itemSelected}
              searchMode={searchMode}
              textQuery={textQuery}
              selectedSearchOption={selectedSearchOption}
              currentLocation={currentLocation}
              currentFilter={currentFilter}
              businessMultipleLocations={businessMultipleLocations}
            />
          )}
        </div>
        <div
          className={`locator-result-popout ${
            itemSelected &&
            (searchResults != null || selectedBuilding != null) &&
            !isSm
              ? "show"
              : ""
          }`}
        >
          <LocatorSearchResultPopout
            community={community}
            item={itemSelected}
            searchMode={searchMode}
            selectedSearchOption={selectedSearchOption}
            onClose={() => setItemSelected(null)}
            currentLocation={currentLocation}
          />
        </div>
        <REListingModal
          name={profile.resourceName}
          reListing={reListingSelected}
          onHide={() => setREListingSelected(null)}
        />
        <MapBoxMapWrapper
          width="100vw"
          height="100vh"
          addMyLocation={true}
          type="MAP"
          defaultCenter={mapDefaultCenter}
          perspective={perspective}
          defaultZoom={12}
          defaultFlyTo={false}
          onMapClicked={onMapClicked}
          // pin layer points are drawn as locator features (see pinLayerFeatures) so they filter like any other layer
          markers={eventMarkers}
          systemLayers={systemLayers}
          onSystemLayerFeatureClick={
            enableBuildingSelection ? onSystemLayerFeatureClick : null
          }
          selectedSystemLayerFeatures={
            selectedBuilding
              ? { BUILDING_FOOTPRINTS: selectedBuilding.id }
              : null
          }
          systemLayerFeatureFilter={
            visibleBuildingIds
              ? { BUILDING_FOOTPRINTS: visibleBuildingIds }
              : null
          }
          mapCenterPadding={20}
          geoJson={backgroundGeoJson}
          onGeoJsonFeatureClicked={null}
          locatorGeoJson={filteredLocatorGeoJson}
          onLocatorGeoJsonFeatureClicked={locatorFeatureClicked}
          hoveredLocatorGeoJsonFeature={itemHovered?.id}
          selectedLocatorGeoJsonFeature={itemSelected?.id ?? reListingSelected?.id}
          boundingBox={boundaryBoundingBox ?? boundingBox}
          mapToggle={{
            assetGroups: true,
            geoJson: true,
            locator: true
          }}
          popupObject={mapPopup}
          horizontalOffset={isSm ? 0 : itemSelected ? screen.width * 0.35 : 0}
        />
      </>
    );
  } else {
    return <></>;
  }
};

export default LocatorContainer;
